import * as Sentry from '@sentry/nextjs';
import { Severity } from '@sentry/types';

type Primitive = string | number | boolean;
type Tags = Record<string, Primitive>;
type Extra = Record<string, unknown>;

interface CaptureOptions {
  stage: string;
  level?: Severity;
  tags?: Tags;
  extra?: Extra;
}

interface SentryLoggerOptions {
  flow?: string;
  defaultTags?: Tags;
}

class SentryLogger {
  private readonly category: string;
  private readonly flow?: string;
  private readonly defaultTags: Tags;

  constructor(category: string, options: SentryLoggerOptions = {}) {
    this.category = category;
    this.flow = options.flow;
    this.defaultTags = options.defaultTags ?? {};
  }

  breadcrumb(
    message: string,
    data?: Extra,
    level: Severity = Severity.Info
  ): void {
    Sentry.addBreadcrumb({
      category: this.category,
      level,
      message,
      data
    });
  }

  captureException(error: unknown, options: CaptureOptions): void {
    Sentry.captureException(error, {
      level: options.level,
      tags: this.buildTags(options.stage, options.tags),
      extra: options.extra
    });
  }

  captureMessage(message: string, options: CaptureOptions): void {
    Sentry.captureMessage(message, {
      level: options.level ?? Severity.Warning,
      tags: this.buildTags(options.stage, options.tags),
      extra: options.extra
    });
  }

  applyScopeTags(): void {
    Sentry.configureScope((scope) => {
      scope.setTag(this.category, true);
      if (this.flow) {
        scope.setTag(`${this.category}-flow`, this.flow);
      }
      Object.entries(this.defaultTags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    });
  }

  private buildTags(stage: string, overrides?: Tags): Tags {
    return {
      [this.category]: true,
      ...(this.flow ? { [`${this.category}-flow`]: this.flow } : {}),
      ...this.defaultTags,
      ...(overrides ?? {}),
      [`${this.category}-stage`]: stage
    };
  }
}

export default SentryLogger;
